// 棚卸しの実数（counted_qty）を CSV から一括投入する。
//
// なぜ要るか＝実数は画面で1件ずつしか入れられず、数千 SKU の店では棚卸し機能が
// 事実上使えない。ハンディ端末の書き出し・Excel の集計表・Stocky のエクスポートは
// どれも CSV で出てくる。
//
// 🔴 **この経路では在庫は動かない。** 実数を埋めるだけで、Shopify への反映は
// 従来どおり「在庫へ反映する」（/apply）だけが行う。
//
// エンドポイントが棚卸しID配下なのは、どの棚卸しの実数かが本質だから
// （移行系の /api/migrate/csv/* とは別系統）。
import { apiFetch } from "./client";
import type {
  CsvApplyResultBase,
  CsvMapping,
  CsvPreview,
  StockyImportArchiveReceipt,
} from "./stocky_csv_import";
import { normalizeStockyImportArchiveReceipt } from "./stocky_csv_import";
import { normalizeStocktake, type Stocktake } from "./stocktakes";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export type { CsvMapping, CsvPreview };

export interface StocktakeCountCsvResult extends CsvApplyResultBase {
  total_rows: number;
  /** 実数を書き込んだ（dry-run では書き込む予定の）明細の件数。 */
  matched_items: number;
  /** CSV にあったが、この棚卸しの対象ではなかったキー。 */
  unmatched_keys: string[];
  invalid_rows: { row: number; reason: string }[];
  /** 🔴 真の件数。上の配列は表示用に切り詰め済み。旧APIでは欠ける。 */
  invalid_row_count?: number;
  unmatched_count?: number;
  dry_run: boolean;
  stocktake_id: StockyPublicBigIntId | null;
  already_imported: boolean;
  source_file_sha256: string;
  source_file_name: string;
  source_import_id: string;
  plan_token: string | null;
  source_archive: StockyImportArchiveReceipt | null;
  normalization_mode: "domain" | "retention_only";
  normalized_rows: number;
  unprocessed_rows: number;
  domain_normalization_complete: boolean;
  retention_reason: string | null;
  /** 取り込み後の棚卸し（dry-run では実数を書いていない状態のまま）。 */
  stocktake: Stocktake;
}

function base(stocktakeId: StockyPublicBigIntId): string {
  return `/api/stocktakes/${canonicalStockyPublicBigIntId(stocktakeId)}/counts/csv`;
}

export function previewStocktakeCountCsv(
  stocktakeId: StockyPublicBigIntId,
  fileBase64: string,
): Promise<CsvPreview> {
  return apiFetch<CsvPreview>(`${base(stocktakeId)}/preview`, {
    method: "POST",
    body: { file_base64: fileBase64 },
  });
}

export async function applyStocktakeCountCsv(
  stocktakeId: StockyPublicBigIntId,
  fileBase64: string,
  mapping: CsvMapping,
  options: {
    dryRun: boolean;
    expectedPlanToken?: string;
    confirmRetentionOnly?: boolean;
    sourceImportId: string;
    sourceFileName: string;
    sourceMediaType: string;
  },
): Promise<StocktakeCountCsvResult> {
  const response = await apiFetch<StocktakeCountCsvResult>(
    `${base(stocktakeId)}/apply`,
    {
      method: "POST",
      body: {
        file_base64: fileBase64,
        mapping,
        dry_run: options.dryRun,
        source_import_id: options.sourceImportId,
        source_file_name: options.sourceFileName,
        source_media_type: options.sourceMediaType,
        ...(options.expectedPlanToken === undefined
          ? {}
          : { expected_plan_token: options.expectedPlanToken }),
        ...(options.confirmRetentionOnly === undefined
          ? {}
          : { confirm_retention_only: options.confirmRetentionOnly }),
      },
    },
  );
  return {
    ...response,
    stocktake_id:
      response.stocktake_id == null
        ? null
        : canonicalStockyPublicBigIntId(response.stocktake_id),
    stocktake: normalizeStocktake(response.stocktake),
    source_archive:
      response.source_archive == null
        ? null
        : normalizeStockyImportArchiveReceipt(response.source_archive),
  };
}
